import type { Language } from '@/i18n/translations';
import { DEFAULT_LANG, stripLangPrefix } from '@/hooks/useLocalizedPath';

export type LegalPageKey = 'legal' | 'privacy' | 'terms' | 'impressum';

export const legalNav: Record<Language, Record<LegalPageKey, { label: string; slug: string }>> = {
  de: {
    legal: { label: 'Rechtliches', slug: 'rechtliches' },
    privacy: { label: 'Datenschutz', slug: 'datenschutz' },
    terms: { label: 'AGB', slug: 'agb' },
    impressum: { label: 'Impressum', slug: 'impressum' },
  },
  en: {
    legal: { label: 'Legal', slug: 'legal' },
    privacy: { label: 'Privacy', slug: 'privacy' },
    terms: { label: 'Terms', slug: 'terms' },
    impressum: { label: 'Impressum', slug: 'impressum' },
  },
  tr: {
    legal: { label: 'Yasal', slug: 'yasal' },
    privacy: { label: 'Gizlilik', slug: 'gizlilik' },
    terms: { label: 'Kullanım Koşulları', slug: 'kullanim-kosullari' },
    impressum: { label: 'Künye', slug: 'kunye' },
  },
  fr: {
    legal: { label: 'Mentions légales', slug: 'mentions-legales' },
    privacy: { label: 'Confidentialité', slug: 'confidentialite' },
    terms: { label: 'CGU', slug: 'cgu' },
    impressum: { label: 'Impressum', slug: 'impressum' },
  },
  ru: {
    legal: { label: 'Правовая информация', slug: 'pravovaya-informatsiya' },
    privacy: { label: 'Конфиденциальность', slug: 'politika-konfidentsialnosti' },
    terms: { label: 'Условия', slug: 'usloviya-i-polozheniya' },
    impressum: { label: 'Выходные данные', slug: 'vykhodnye-dannye' },
  },
  it: {
    legal: { label: 'Note legali', slug: 'note-legali' },
    privacy: { label: 'Privacy', slug: 'informativa-privacy' },
    terms: { label: 'Termini', slug: 'termini-e-condizioni' },
    impressum: { label: 'Impressum', slug: 'impressum' },
  },
  es: {
    legal: { label: 'Aviso legal', slug: 'aviso-legal' },
    privacy: { label: 'Privacidad', slug: 'politica-de-privacidad' },
    terms: { label: 'Términos', slug: 'terminos-y-condiciones' },
    impressum: { label: 'Impressum', slug: 'impressum' },
  },
  ar: {
    legal: { label: 'معلومات قانونية', slug: 'malumat-qanuniya' },
    privacy: { label: 'سياسة الخصوصية', slug: 'siyasat-alkhususiya' },
    terms: { label: 'الشروط والأحكام', slug: 'alshurut-walahkam' },
    impressum: { label: 'بيانات النشر', slug: 'bayanat-alnashr' },
  },
};

export const getLegalLinks = (lang: Language) => {
  const nav = legalNav[lang] ?? legalNav[DEFAULT_LANG as Language];
  return [
    { key: 'legal' as const, label: nav.legal.label, href: `/${nav.legal.slug}` },
    { key: 'privacy' as const, label: nav.privacy.label, href: `/${nav.privacy.slug}` },
    { key: 'terms' as const, label: nav.terms.label, href: `/${nav.terms.slug}` },
    { key: 'impressum' as const, label: nav.impressum.label, href: `/${nav.impressum.slug}` },
  ];
};

export const LEGAL_ROUTE_PATHS = Array.from(
  new Set(Object.values(legalNav).flatMap((pages) => Object.values(pages).map((page) => page.slug)))
);

export const getLegalPageKeyFromSlug = (slug: string): LegalPageKey | null => {
  for (const pages of Object.values(legalNav)) {
    for (const [key, page] of Object.entries(pages) as [LegalPageKey, { label: string; slug: string }][]) {
      if (page.slug === slug) return key;
    }
  }
  return null;
};

export const getLocalizedPathForLang = (pathname: string, targetLang: Language): string => {
  const cleanPath = stripLangPrefix(pathname);
  const cleanSlug = cleanPath.replace(/^\//, '').replace(/\/$/, '');
  const legalKey = getLegalPageKeyFromSlug(cleanSlug);

  if (legalKey) {
    return `/${targetLang}/${legalNav[targetLang][legalKey].slug}`;
  }

  return `/${targetLang}${cleanPath === '/' ? '' : cleanPath}`;
};

export const getDefaultLocalizedPath = (pathname: string) => getLocalizedPathForLang(pathname, DEFAULT_LANG as Language);
